import type { TreeNodeData } from "@mantine/core";
import type { Relation, Schema } from "./engine";

// One line of the pane. The tree draws these and knows nothing about databases, which is what lets the same rendering
// answer for a Postgres catalogue and for a handful of SQLite pragmas.
export interface Row {
  name: string;
  // A dimmed word after the name: a column's type, a relation's kind, a count under a heading.
  kind?: string;
  // The rest of the line, in the page's brightest ink, since it is what somebody opened the branch to read.
  detail?: string;
  // Short words a column is flagged with, drawn as badges rather than run into the detail.
  marks?: string[];
  // A definition, which is a paragraph of SQL rather than a row, and is laid out as one.
  block?: boolean;
}

export interface SchemaNode extends TreeNodeData {
  row: Row;
  children?: SchemaNode[];
}

// The whole catalogue as the pane draws it. Values are paths built from names, so a refresh after a statement leaves
// every branch somebody opened still open — the nodes are new objects but they answer to the same values.
export function schemaTree(schemas: Schema[]): SchemaNode[] {
  return schemas.map((schema) => ({
    value: `schema:${schema.name}`,
    label: schema.name,
    row: { name: schema.name, kind: count(schema.relations.length, "relation") },
    children: schema.relations.map((relation) => relationNode(`schema:${schema.name}`, relation)),
  }));
}

// Which branches a fresh session opens on: every schema, every relation, and the columns under each — the three
// levels somebody would otherwise open one by one before the pane told them anything.
export function defaultOpen(nodes: SchemaNode[]): string[] {
  const values: string[] = [];

  for (const schema of nodes) {
    values.push(schema.value);
    for (const relation of schema.children ?? []) {
      values.push(relation.value);
      const columns = relation.children?.find((section) => section.value.endsWith("/columns"));
      if (columns) values.push(columns.value);
    }
  }

  return values;
}

function relationNode(path: string, relation: Relation): SchemaNode {
  const at = `${path}/relation:${relation.name}`;
  const sections: SchemaNode[] = [];

  if (relation.columns.length > 0) {
    sections.push({
      value: `${at}/columns`,
      label: "Columns",
      row: { name: "Columns", kind: String(relation.columns.length) },
      children: relation.columns.map((column) => ({
        value: `${at}/columns/${column.name}`,
        label: column.name,
        row: {
          name: column.name,
          kind: column.type,
          marks: [column.primaryKey ? "PK" : "", column.notNull ? "NOT NULL" : ""].filter(Boolean),
          detail: column.fallback ? `DEFAULT ${column.fallback}` : undefined,
        },
      })),
    });
  }

  if (relation.indexes.length > 0) {
    sections.push({
      value: `${at}/indexes`,
      label: "Indexes",
      row: { name: "Indexes", kind: String(relation.indexes.length) },
      children: relation.indexes.map((index) => ({
        value: `${at}/indexes/${index.name}`,
        label: index.name,
        row: {
          name: index.name,
          kind: index.primary ? "primary" : index.unique ? "unique" : "index",
          detail: `(${index.columns.join(", ")})`,
        },
      })),
    });
  }

  if (relation.constraints.length > 0) {
    sections.push({
      value: `${at}/constraints`,
      label: "Constraints",
      row: { name: "Constraints", kind: String(relation.constraints.length) },
      // Two CHECKs on one table are two rows with the same kind, so the position is part of what tells them apart.
      children: relation.constraints.map((constraint, index) => ({
        value: `${at}/constraints/${index}`,
        label: constraint.kind,
        row: { name: constraint.kind, kind: constraint.name || undefined, detail: constraint.detail },
      })),
    });
  }

  if (relation.definition) {
    sections.push({
      value: `${at}/definition`,
      label: "Definition",
      row: { name: "Definition" },
      children: [{
        value: `${at}/definition/sql`,
        label: relation.definition,
        row: { name: relation.definition.trim(), block: true },
      }],
    });
  }

  return {
    value: at,
    label: relation.name,
    row: { name: relation.name, kind: relation.kind, detail: count(relation.columns.length, "column") },
    children: sections,
  };
}

function count(total: number, noun: string): string {
  return `${total} ${noun}${total === 1 ? "" : "s"}`;
}
